(() => {
const feUi = JSON.parse(document.getElementById('fe-landing-ui-strings')?.textContent || '{}');
// No dependencies; one timer and only the next image prepared in advance.
if (!customElements.get('fe-v8-slideshow')) {
  customElements.define('fe-v8-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const opts = {signal: this.events.signal};
      this.slides = [...this.querySelectorAll('[data-fe-slide]')];
      this.button = this.querySelector('[data-fe-slideshow-toggle]');
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.request = 0;
      this.visible = false;
      this.focused = false;
      this.editorSelected = false;
      this.userPaused = this.dataset.autoplay === 'false';
      this.hovered = false;
      this.explicitPlay = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.interval = Math.max(4000, Number(this.dataset.interval) || 6000);
      this.pending = new Map();
      this.update = this.update.bind(this);
      if (this.slides.length < 2) return;
      this.bindDrag(opts);
      this.addEventListener('focusin', e => {
        this.focused = e.target.matches(':focus-visible');
        this.update();
      }, opts);
      this.addEventListener('focusout', e => {
        this.focused = !!e.relatedTarget && this.contains(e.relatedTarget) && e.relatedTarget.matches(':focus-visible');
        this.update();
      }, opts);
      document.addEventListener('visibilitychange', this.update, opts);
      this.reduced.addEventListener('change', this.update, opts);
      document.addEventListener('shopify:block:select', e => {
        const target = this.slides.findIndex(slide => slide.dataset.blockId === e.detail?.blockId);
        if (target < 0) return;
        this.editorSelected = true;
        this.update();
        this.show(target, false);
      }, opts);
      document.addEventListener('shopify:block:deselect', e => {
        if (!this.slides.some(slide => slide.dataset.blockId === e.detail?.blockId)) return;
        this.editorSelected = false;
        this.update();
      }, opts);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.update();
      }, {threshold: .15});
      this.observer.observe(this);
      this.update();
    }
    bindDrag(opts) {
      this.addEventListener('pointerenter',e=>{if(e.pointerType==='mouse'){this.hovered=true;this.update();}},opts);
      this.addEventListener('pointerleave',e=>{if(e.pointerType==='mouse'){this.hovered=false;this.gesture=null;this.removeAttribute('data-dragging');this.update();}},opts);
      this.addEventListener('dragstart', e => e.preventDefault(), opts);
      this.addEventListener('pointerdown', e => {
        if (!e.isPrimary || e.button !== 0) return;
        this.focused = false;
        this.gesture = {id:e.pointerId,x:e.clientX,y:e.clientY,drag:false}; this.update();
      }, opts);
      this.addEventListener('pointermove', e => {
        const g=this.gesture; if(!g || e.pointerId!==g.id) return;
        const dx=e.clientX-g.x,dy=e.clientY-g.y;
        if(!g.drag && Math.abs(dy)>12 && Math.abs(dy)>Math.abs(dx)){this.gesture=null;this.update();return;}
        if(Math.abs(dx)>12 && Math.abs(dx)>Math.abs(dy)*1.2){
          g.drag=true; this.setAttribute('data-dragging','');
          if(!this.hasPointerCapture(e.pointerId)) this.setPointerCapture(e.pointerId);
        }
      },opts);
      const finish=e=>{
        const g=this.gesture; this.gesture=null; this.removeAttribute('data-dragging'); this.update();
        if(!g || e.pointerId!==g.id) return;
        if(this.hasPointerCapture(e.pointerId)) this.releasePointerCapture(e.pointerId);
        if(g.drag){
          this.suppressClickUntil=Date.now()+500;
          const dx=e.clientX-g.x;
          if(e.type==='pointerup' && Math.abs(dx)>=45) this.show((this.index+(dx<0?1:-1)+this.slides.length)%this.slides.length,false);
        }
      };
      this.addEventListener('pointerup',finish,opts);
      this.addEventListener('pointercancel',finish,opts);
      this.addEventListener('click',e=>{if(Date.now()<(this.suppressClickUntil||0)){e.preventDefault();e.stopImmediatePropagation();}},{...opts,capture:true});
      this.addEventListener('keydown',e=>{
        if(e.key!=='ArrowLeft' && e.key!=='ArrowRight')return;
        e.preventDefault();this.show((this.index+(e.key==='ArrowRight'?1:-1)+this.slides.length)%this.slides.length,false);
      },opts);
    }
    canRun() {
      return this.isConnected && this.slides.length > 1 && this.visible && !document.hidden &&
        !this.userPaused && !this.focused && !this.editorSelected && !this.hovered && !this.gesture &&
        (!this.reduced.matches || this.explicitPlay);
    }
    update() {
      clearTimeout(this.timer);
      const stopped = this.userPaused || (this.reduced.matches && !this.explicitPlay);
      this.toggleAttribute('data-paused', stopped);
      const label = stopped ? feUi.playSlides : feUi.pauseSlides;
      this.button?.setAttribute('aria-label', label);
      this.button?.setAttribute('title', label);
      if (!this.canRun()) return;
      this.prepareLater((this.index + 1) % this.slides.length);
      this.timer = setTimeout(() => this.show((this.index + 1) % this.slides.length, true), this.interval);
    }
    prepareLater(index) {
      // Slide-ul următor pornește abia după încărcarea paginii, ca să nu concureze cu imaginea primului banner (LCP).
      if (document.readyState === 'complete') { this.prepare(index); return; }
      addEventListener('load', () => this.isConnected && this.prepare(index), {once: true, signal: this.events.signal});
    }
    prepare(index) {
      if (this.pending.has(index)) return this.pending.get(index);
      const slide = this.slides[index];
      const template = slide.querySelector('template[data-fe-slide-media]');
      if (template) { template.replaceWith(template.content.cloneNode(true)); }
      const img = slide.querySelector('picture img');
      const promise = !img ? Promise.resolve(false) : new Promise(resolve => {
        if (img.complete) { resolve(img.naturalWidth > 0); return; }
        const finish = ok => { clearTimeout(timeout); resolve(ok); };
        const timeout = setTimeout(() => resolve(false), 15000);
        img.addEventListener('load', () => finish(true), {once: true, signal: this.events.signal});
        img.addEventListener('error', () => finish(false), {once: true, signal: this.events.signal});
        this.events.signal.addEventListener('abort', () => finish(false), {once: true});
      });
      this.pending.set(index, promise);
      return promise;
    }
    async show(index, automatic) {
      const request = ++this.request;
      let usable = false;
      // Keep the current banner if the incoming asset fails; try the next usable banner.
      for (let attempt = 0; attempt < this.slides.length; attempt++) {
        const ready = await this.prepare(index);
        if (!this.isConnected || request !== this.request || (automatic && !this.canRun())) return;
        if (ready) { usable = true; break; }
        index = (index + 1) % this.slides.length;
      }
      if (!usable || index === this.index) { this.update(); return; }
      clearTimeout(this.cleanup);
      this.slides.forEach(slide => slide.classList.remove('is-leaving'));
      const old = this.slides[this.index];
      old.classList.add('is-leaving');
      old.classList.remove('is-active');
      old.setAttribute('aria-hidden', 'true');
      if (old.contains(document.activeElement)) this.focus({preventScroll:true});
      old.inert = true;
      const next = this.slides[index];
      next.inert = false;
      next.removeAttribute('aria-hidden');
      next.classList.add('is-active');
      this.index = index;
      this.cleanup = setTimeout(() => old.classList.remove('is-leaving'), 1250);
      this.update();
    }
    disconnectedCallback() {
      this.request++;
      clearTimeout(this.timer);
      clearTimeout(this.cleanup);
      this.events?.abort();
      this.observer?.disconnect();
    }
  });
}

})();
